import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShoppingBag, Minus, Plus, AlertTriangle, Sparkles, Star, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCart, brl } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { slug } = useParams();
  const { add, buyNow, count, setOpen } = useCart();
  const [p, setP] = useState(undefined);
  const [qty, setQty] = useState(1);
  const [mainImg, setMainImg] = useState(null);
  const [related, setRelated] = useState([]);
  const [variant, setVariant] = useState(null);
  const [reviewsData, setReviewsData] = useState({ average: 0, count: 0, reviews: [] });
  const [reviewForm, setReviewForm] = useState({ name: "", email: "", rating: 0, comment: "" });
  const [reviewMsg, setReviewMsg] = useState(null);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);

  const loadReviews = () => {
    fetch(`${API}/products/${slug}/reviews`).then((r) => r.json()).then(setReviewsData).catch(() => {});
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(undefined);
    setQty(1);
    setReviewMsg(null);
    setReviewForm({ name: "", email: "", rating: 0, comment: "" });
    loadReviews();
    fetch(`${API}/products/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((prod) => {
        setP(prod);
        setMainImg(prod.image);
        fetch(`${API}/products`)
          .then((r) => r.json())
          .then((all) => setRelated(all.filter((x) => x.category === prod.category && x.slug !== prod.slug).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setP(null));
  }, [slug]);

  // Galeria completa: foto principal primeiro, depois fotos dos sabores e as demais
  const gallery = p ? [...new Set([variant?.image || p.image, ...(p.variants || []).map((v) => v.image).filter(Boolean), ...(p.gallery || [])])] : [];
  const goImg = (dir) => {
    setMainImg((cur) => {
      if (gallery.length < 2) return cur;
      const i = gallery.indexOf(cur);
      return gallery[(Math.max(i, 0) + dir + gallery.length) % gallery.length];
    });
  };

  // Teclado no modo ampliado: Esc fecha, setas navegam
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
      else if (e.key === "ArrowRight") goImg(1);
      else if (e.key === "ArrowLeft") goImg(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewMsg(null);
    try {
      const r = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, ...reviewForm }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Não foi possível enviar.");
      setReviewMsg({ ok: true, text: data.message });
      setReviewForm({ name: "", email: "", rating: 0, comment: "" });
    } catch (err) {
      setReviewMsg({ ok: false, text: err.message });
    }
  };

  if (p === undefined) {
    return <div className="min-h-screen bg-offwhite flex items-center justify-center text-military-dark/50 text-sm">Carregando...</div>;
  }
  if (p === null) {
    return (
      <div className="min-h-screen bg-offwhite flex flex-col items-center justify-center gap-6" data-testid="product-not-found">
        <p className="font-serif italic text-3xl text-military-dark">Produto não encontrado.</p>
        <Link to="/" className="rounded-full bg-military text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold">Voltar para a loja</Link>
      </div>
    );
  }

  const variants = p.variants || [];
  const onSale = variant
    ? variant.sale_price != null && variant.sale_price < (variant.price ?? p.price)
    : p.sale_price != null && p.sale_price < p.price;
  const listPrice = variant ? variant.price ?? p.price : p.price;
  const effective = onSale ? (variant ? variant.sale_price : p.sale_price) : listPrice;
  const stockNow = variant ? variant.stock ?? 0 : p.stock;
  const out = stockNow === 0;
  // Dados exibidos: o sabor selecionado sobrescreve apenas o que ele tiver cadastrado; o resto usa as informações gerais do produto (nunca as de outro sabor)
  const variantNutrition = Array.isArray(variant?.nutrition) && variant.nutrition.length > 0;
  const variantIngredients = !!(variant?.ingredients || "").trim();
  const variantAllergens = !!(variant?.allergens || "").trim();
  const nutritionData = variantNutrition ? variant.nutrition : p.nutrition;
  const doseData = (variant?.dose || "").trim() || p.dose;
  const ingredientsData = variantIngredients ? variant.ingredients : p.composition;
  const allergensData = variantAllergens ? variant.allergens : p.warnings;

  return (
    <div data-testid="product-page" className="min-h-screen bg-offwhite font-sans">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-military/10">
        <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <Link to="/" data-testid="product-back-link" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/70 hover:text-military-dark transition-colors">
            <ArrowLeft size={16} /> Voltar para a loja
          </Link>
          <Link to="/" className="font-serif italic text-2xl text-military-dark hidden sm:block">leve <span className="not-italic font-semibold text-military">essenza</span></Link>
          <button data-testid="product-cart-button" onClick={() => setOpen(true)} className="relative flex items-center gap-2 rounded-full bg-military text-offwhite px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">
            <ShoppingBag size={14} /> Sacola
            {count > 0 && <span className="absolute -top-1.5 -right-1.5 bg-baby text-military-dark text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
          </button>
        </div>
      </header>

      <main className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }} className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-lg bg-white border border-military/10">
              <div className="absolute inset-0 spotlight pointer-events-none" />
              <img data-testid="product-image" src={mainImg} alt={p.name} onClick={() => setZoom(true)} className="w-full aspect-[4/3] object-cover cursor-zoom-in" />
              {gallery.length > 1 && (
                <>
                  <button data-testid="gallery-prev" aria-label="Foto anterior" onClick={() => goImg(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 backdrop-blur border border-military/15 text-military-dark flex items-center justify-center hover:bg-white transition-colors"><ChevronLeft size={18} /></button>
                  <button data-testid="gallery-next" aria-label="Próxima foto" onClick={() => goImg(1)} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 backdrop-blur border border-military/15 text-military-dark flex items-center justify-center hover:bg-white transition-colors"><ChevronRight size={18} /></button>
                </>
              )}
              {p.tag && <span className="absolute top-4 left-4 bg-baby text-military-dark text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">{p.tag}</span>}
              {out && <span data-testid="product-out-of-stock" className="absolute top-4 right-4 bg-military-dark text-offwhite text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">Esgotado</span>}
              {onSale && !out && <span className="absolute top-4 right-4 bg-military text-offwhite text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">Promoção</span>}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4" data-testid="product-gallery">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setMainImg(g)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${mainImg === g ? "border-military" : "border-transparent opacity-60 hover:opacity-100"}`}
                  >
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {ingredientsData && (
              <div key={`ing-${variant?.name || "geral"}`} className="mt-8 bg-white border border-military/10 rounded-lg p-6" data-testid="ingredients-card">
                <p className="text-[10px] uppercase tracking-[0.25em] text-military font-semibold mb-2 flex items-center gap-2"><Sparkles size={12} /> Ingredientes{variantIngredients ? ` — ${variant.name}` : ""}</p>
                <p data-testid="product-composition" className="text-sm text-military-dark/70 leading-relaxed">{ingredientsData}</p>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.76, 0, 0.24, 1] }} className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4">{p.category}</p>
            <h1 data-testid="product-name" className="font-serif text-4xl md:text-6xl text-military-dark tracking-tight leading-[1.02] mb-6">{p.name}</h1>
            <p className="text-base md:text-lg text-military-dark/70 leading-relaxed mb-8 max-w-xl">{p.description}</p>

            <div className="flex items-end gap-4 mb-3">
              {onSale && <p className="text-lg text-military-dark/40 line-through">{brl(p.price)}</p>}
              <p data-testid="product-price" className="font-serif text-5xl text-military-dark">{brl(effective)}</p>
            </div>
            <p className="text-xs text-military-dark/50 mb-8" data-testid="product-stock-line">
              {out ? "Produto indisponível no momento" : stockNow <= (variant?.min_stock ?? p.min_stock ?? 5) ? `Últimas ${stockNow} unidades em estoque` : "Em estoque — envio rápido"}
            </p>

            {variants.length > 0 && (
              <div className="mb-8" data-testid="variant-selector">
                <p className="text-[10px] uppercase tracking-[0.25em] text-military-dark/50 font-semibold mb-3">
                  Sabor{variant ? `: ${variant.name}` : ""}
                </p>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <button
                      key={v.name}
                      data-testid={`variant-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => { setVariant(v); if (v.image) setMainImg(v.image); }}
                      className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-colors duration-300 ${
                        variant?.name === v.name ? "bg-military text-offwhite" : "bg-white border border-military/20 text-military-dark/70 hover:bg-baby hover:text-military-dark"
                      } ${v.stock === 0 ? "opacity-40 line-through" : ""}`}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <AnimatePresence mode="wait">
              {variant?.description ? (
                <motion.div
                  key={variant.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
                  className="mb-8 max-w-xl bg-white border border-military/10 rounded-lg p-5"
                  data-testid="variant-description"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-military font-semibold mb-2">Sabor {variant.name}</p>
                  <p className="text-sm text-military-dark/70 leading-relaxed">{variant.description}</p>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-4 border border-military/20 rounded-full px-4 py-3">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1 hover:text-military transition-colors"><Minus size={14} /></button>
                <span data-testid="qty-value" className="text-sm font-semibold w-5 text-center">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(stockNow || 1, q + 1))} className="p-1 hover:text-military transition-colors"><Plus size={14} /></button>
              </div>
              <button
                data-testid="product-add-button"
                disabled={out}
                onClick={() => add({ ...p, price: effective }, qty, variant)}
                className="flex-1 min-w-[200px] rounded-full border border-military-dark text-military-dark py-4 text-sm uppercase tracking-widest font-semibold hover:bg-baby transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ShoppingBag size={16} /> {out ? "Indisponível" : "Adicionar à sacola"}
              </button>
              {!out && (
                <button
                  data-testid="product-buy-now-button"
                  onClick={() => buyNow({ ...p, price: effective }, qty, variant)}
                  className="flex-1 min-w-[200px] rounded-full bg-military-dark text-offwhite py-4 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors duration-300"
                >
                  Comprar agora
                </button>
              )}
            </div>
            <p className="text-xs text-military-dark/45 leading-relaxed max-w-xl">Este produto não substitui uma alimentação equilibrada. Consulte um médico ou nutricionista antes de iniciar o uso.</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mt-24">
          {p.usage && (
            <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-5" data-testid="usage-section">
              <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4">Modo de uso</p>
              <div className="border-l-2 border-military pl-6">
                <p className="font-serif text-xl md:text-2xl text-military-dark leading-relaxed">{p.usage}</p>
              </div>
            </motion.section>
          )}
          {allergensData && (
            <motion.section key={`warn-${variant?.name || "geral"}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-7" data-testid="warnings-section">
              <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4">Advertências{variantAllergens ? ` — ${variant.name}` : ""}</p>
              <div className="bg-baby border border-military/15 rounded-lg p-6 flex gap-4">
                <AlertTriangle size={20} className="text-military-dark shrink-0 mt-0.5" />
                <p data-testid="warnings-text" className="text-sm text-military-dark/80 leading-relaxed">{allergensData}</p>
              </div>
            </motion.section>
          )}
        </div>

        {nutritionData && nutritionData.length > 0 && (
          <motion.section key={`nut-${variant?.name || "geral"}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mt-24" data-testid="nutrition-section">
            <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-2">Tabela nutricional{variantNutrition ? ` — ${variant.name}` : ""}</p>
            {doseData && <p data-testid="nutrition-dose" className="text-sm text-military-dark/50 mb-6">Porção: {doseData}</p>}
            <div className="bg-white border border-military/15 rounded-lg overflow-hidden max-w-2xl">
              <table className="w-full text-sm" data-testid="nutrition-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-military-dark/50 border-b border-military/15 bg-offwhite">
                    <th className="px-5 py-3">Componente</th>
                    <th className="px-5 py-3">Por dose</th>
                    <th className="px-5 py-3 text-right">%VD*</th>
                  </tr>
                </thead>
                <tbody>
                  {nutritionData.map((n, i) => (
                    <tr key={i} className="border-b border-military/10 last:border-0">
                      <td className="px-5 py-3 text-military-dark font-medium">{n.label}</td>
                      <td className="px-5 py-3 text-military-dark/70">{n.per_dose}</td>
                      <td className="px-5 py-3 text-right text-military-dark/70">{n.vd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-5 py-3 text-[10px] text-military-dark/40 border-t border-military/10">* % de Valores Diários de referência com base em uma dieta de 2.000 kcal. "—" valor diário não estabelecido.</p>
            </div>
          </motion.section>
        )}
        {p.extra_info && (
          <section data-testid="extra-info-section" className="mt-16 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4">Informações adicionais</p>
            <p className="text-sm text-military-dark/70 leading-relaxed">{p.extra_info}</p>
          </section>
        )}

        <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mt-24" data-testid="reviews-section">
          <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-2">Avaliações</p>
          <div className="flex items-end gap-4 mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-military-dark">Quem comprou, opinou</h2>
            {reviewsData.count > 0 && (
              <p className="flex items-center gap-2 text-sm text-military-dark/60 pb-1" data-testid="reviews-average">
                <span className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < Math.round(reviewsData.average) ? "fill-military text-military" : "text-military/25"} />)}
                </span>
                {reviewsData.average} · {reviewsData.count} {reviewsData.count === 1 ? "avaliação" : "avaliações"}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-6" data-testid="reviews-list">
              {reviewsData.reviews.map((r) => (
                <div key={r.id} className="border-b border-military/10 pb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => <Star key={i} size={13} className={i < r.rating ? "fill-military text-military" : "text-military/25"} />)}
                    </span>
                    <p className="text-sm font-semibold text-military-dark">{r.name}</p>
                    <span className="text-[10px] uppercase tracking-wider text-military font-semibold">compra verificada</span>
                  </div>
                  <p className="text-sm text-military-dark/70 leading-relaxed">{r.comment}</p>
                </div>
              ))}
              {reviewsData.count === 0 && <p className="font-serif italic text-xl text-military-dark/40">Seja a primeira pessoa a avaliar este produto.</p>}
            </div>

            <form onSubmit={submitReview} className="lg:col-span-5 bg-white border border-military/10 rounded-lg p-6 md:p-8 self-start" data-testid="review-form">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-military-dark/60 mb-5">Deixe sua avaliação</p>
              <div className="flex gap-1.5 mb-5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setReviewForm({ ...reviewForm, rating: n })} className="p-1">
                    <Star size={22} className={n <= reviewForm.rating ? "fill-military text-military" : "text-military/25"} />
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                <input data-testid="review-name" required placeholder="Seu nome" value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} className="w-full bg-offwhite border border-military/20 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-military" />
                <input data-testid="review-email" required type="email" placeholder="E-mail usado na compra" value={reviewForm.email} onChange={(e) => setReviewForm({ ...reviewForm, email: e.target.value })} className="w-full bg-offwhite border border-military/20 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-military" />
                <textarea data-testid="review-comment" rows={3} placeholder="Conte como foi sua experiência (opcional)" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} className="w-full bg-offwhite border border-military/20 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-military" />
              </div>
              <p className="text-[11px] text-military-dark/45 mt-3 leading-relaxed">Exclusivo para quem comprou: validamos o e-mail do seu pedido. A avaliação é publicada após aprovação da loja.</p>
              {reviewMsg && <p data-testid="review-message" className={`text-sm mt-3 ${reviewMsg.ok ? "text-military" : "text-red-700"}`}>{reviewMsg.text}</p>}
              <button data-testid="review-submit" type="submit" disabled={reviewForm.rating === 0} className="mt-5 w-full rounded-full bg-military-dark text-offwhite py-3.5 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors disabled:opacity-30">
                Enviar avaliação
              </button>
            </form>
          </div>
        </motion.section>

        {related.length > 0 && (
          <section data-testid="related-products" className="mt-24">
            <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-2">Combine com</p>
            <h2 className="font-serif text-3xl md:text-4xl text-military-dark mb-10">Produtos relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((r) => {
                const rSale = r.sale_price != null && r.sale_price < r.price;
                return (
                  <Link key={r.slug} to={`/produto/${r.slug}`} data-testid={`related-${r.slug}`} className="group bg-white border border-military/10 rounded-lg overflow-hidden hover:border-military/30 transition-colors">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img src={r.image} alt={r.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      {r.stock === 0 && <span className="absolute top-3 right-3 bg-military-dark text-offwhite text-[9px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded-full">Esgotado</span>}
                    </div>
                    <div className="p-5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-military font-semibold mb-1">{r.category}</p>
                      <p className="font-serif text-xl text-military-dark leading-tight mb-2">{r.name}</p>
                      <p className="text-sm font-semibold text-military-dark">
                        {rSale && <span className="text-military-dark/40 line-through mr-2 font-normal">{brl(r.price)}</span>}
                        {brl(rSale ? r.sale_price : r.price)}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <AnimatePresence>
        {zoom && mainImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-military-dark/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setZoom(false)}
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX.current == null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (dx > 50) goImg(-1);
              else if (dx < -50) goImg(1);
              touchX.current = null;
            }}
          >
            <button data-testid="lightbox-close" aria-label="Fechar" onClick={() => setZoom(false)} className="absolute top-5 right-5 text-offwhite/70 hover:text-offwhite transition-colors z-10"><X size={28} /></button>
            {gallery.length > 1 && (
              <>
                <button data-testid="lightbox-prev" aria-label="Foto anterior" onClick={(e) => { e.stopPropagation(); goImg(-1); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-offwhite/10 hover:bg-offwhite/20 text-offwhite flex items-center justify-center transition-colors z-10"><ChevronLeft size={22} /></button>
                <button data-testid="lightbox-next" aria-label="Próxima foto" onClick={(e) => { e.stopPropagation(); goImg(1); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-offwhite/10 hover:bg-offwhite/20 text-offwhite flex items-center justify-center transition-colors z-10"><ChevronRight size={22} /></button>
              </>
            )}
            <motion.img
              key={mainImg}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              src={mainImg}
              alt={p.name}
              data-testid="lightbox-image"
              className="max-h-[82vh] max-w-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {gallery.length > 1 && (
              <p data-testid="lightbox-counter" className="absolute bottom-5 left-1/2 -translate-x-1/2 text-offwhite/60 text-xs tracking-[0.3em]">{gallery.indexOf(mainImg) + 1} / {gallery.length}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
